// Dedicated page for company-specific tickets
import React, { useState, useEffect } from 'react';
import { fetchTickets } from '../api/pocketbase';
import { FaFilter, FaStar, FaSortAmountDown, FaArrowLeft, FaCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

const PAGE_SIZE = 10;

// Skeleton loading component
const SkeletonRow = () => (
    <tr className="border-b hover:bg-gray-50">
        <td className="py-4 px-6"><div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div></td>
        <td className="py-4 px-6"><div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div></td>
        <td className="py-4 px-6"><div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div></td>
        <td className="py-4 px-6"><div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div></td>
        <td className="py-4 px-6"><div className="h-4 bg-gray-200 rounded animate-pulse w-40"></div></td>
        <td className="py-4 px-6"><div className="h-6 bg-gray-200 rounded-full animate-pulse w-20"></div></td>
        <td className="py-4 px-6"><div className="h-6 bg-gray-200 rounded-full animate-pulse w-16"></div></td>
    </tr>
);

const CompanyTicketsView = () => {
    const navigate = useNavigate();
    const { id: companyId } = useParams();
    const [tickets, setTickets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('open'); // 'open' or 'close'
    const [sortOrder, setSortOrder] = useState('needs_reply'); // default to Needs Reply First
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch tickets when filters change
    useEffect(() => {
        const loadTickets = async () => {
            setIsLoading(true);
            try {
                // Pass sortOrder as the 3rd argument, companyId as 2nd
                const data = await fetchTickets(filterStatus, companyId, sortOrder);
                setTickets(data);
                setCurrentPage(1); // Reset to page 1 whenever filters change
            } catch (error) {
                console.error('Error loading tickets:', error);
            } finally {
                setIsLoading(false);
            }
        };
        if (companyId) {
            loadTickets();
        }
    }, [filterStatus, companyId, sortOrder]);

    const handleFilterChange = (e) => {
        setFilterStatus(e.target.value);
    };

    const handleSortChange = (e) => {
        setSortOrder(e.target.value);
    };

    // Pagination derived values
    const totalPages = Math.max(1, Math.ceil(tickets.length / PAGE_SIZE));
    const paginatedTickets = tickets.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const goToPage = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    // Build page number buttons (show max 5 around current page)
    const getPageNumbers = () => {
        const pages = [];
        const delta = 2;
        const left = Math.max(1, currentPage - delta);
        const right = Math.min(totalPages, currentPage + delta);
        for (let i = left; i <= right; i++) pages.push(i);
        return pages;
    };

    const renderStars = (priority) => {
        // Priority 0 or 1 = 1 star.
        const count = priority && priority > 0 ? priority : 1;
        // Limit to 5 just in case
        const safeCount = Math.min(count, 5);
        
        return (
            <div className="flex gap-0.5" title={`Priority: ${safeCount}`}>
                {Array.from({ length: safeCount }).map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 text-xs" />
                ))}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Header */}
            <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 md:px-10 py-4 sm:py-6 bg-white shadow-sm rounded-bl-3xl rounded-br-3xl mb-6 sm:mb-8 gap-4 sm:gap-0">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate('/companies')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <FaArrowLeft className="text-gray-600" />
                    </button>
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Company Tickets</h1>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
                    {/* Legend */}
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <FaCircle className="text-green-500 text-[8px]" />
                        Needs Reply (Last reply from customer)
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 px-2 sm:px-4 md:px-10 pb-6 sm:pb-10 w-full">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
                    
                    {/* Filter Section */}
                    <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h3 className="text-gray-900 font-bold text-lg">Ticket Management</h3>
                            <p className="text-gray-500 text-sm mt-1">
                                {isLoading 
                                    ? 'Loading tickets...' 
                                    : `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, tickets.length)}–${Math.min(currentPage * PAGE_SIZE, tickets.length)} of ${tickets.length} ${filterStatus} tickets`}
                            </p>
                        </div>
                        <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center lg:w-auto">
                            <FaFilter className="text-gray-400 hidden sm:block" />
                            
                            {/* Sort Filter */}
                             <div className="relative">
                                <FaSortAmountDown className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                <select
                                    value={sortOrder}
                                    onChange={handleSortChange}
                                    className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#90c740] focus:border-transparent appearance-none"
                                >
                                    <option value="created_desc">Newest First</option>
                                    <option value="created_asc">Oldest First</option>
                                    <option value="updated_desc">Latest Answered</option>
                                    <option value="updated_asc">Oldest Answered</option>
                                    <option value="priority_desc">Highest Priority</option>
                                    <option value="priority_asc">Lowest Priority</option>
                                </select>
                             </div>

                            {/* Status Filter */}
                            <select
                                value={filterStatus}
                                onChange={handleFilterChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#90c740] focus:border-transparent sm:w-auto"
                            >
                                <option value="open">Open Tickets</option>
                                <option value="close">Closed Tickets</option>
                            </select>
                        </div>
                    </div>

                    {/* Mobile Cards */}
                    <div className="space-y-4 p-4 md:hidden">
                        {isLoading ? (
                            Array.from({ length: PAGE_SIZE }).map((_, index) => (
                                <div key={index} className="rounded-xl border border-gray-200 p-4 shadow-sm">
                                    <div className="space-y-3">
                                        <div className="h-5 w-24 animate-pulse rounded bg-gray-200"></div>
                                        <div className="h-4 w-full animate-pulse rounded bg-gray-100"></div>
                                        <div className="h-4 w-3/4 animate-pulse rounded bg-gray-100"></div>
                                    </div>
                                </div>
                            ))
                        ) : paginatedTickets.length > 0 ? (
                            paginatedTickets.map((ticket) => {
                                const needsReply = ticket.is_support_reply !== true;
                                return (
                                    <button
                                        key={ticket.id}
                                        type="button"
                                        onClick={() => navigate(`/tickets/${ticket.id}`)}
                                        className={`w-full rounded-xl border border-gray-200 p-4 text-left shadow-sm transition hover:border-[#90c740] hover:shadow-md ${
                                            needsReply ? "bg-green-50/40" : "bg-white"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    {needsReply && <FaCircle className="text-[9px] text-green-500 flex-shrink-0" />}
                                                    <h3 className="truncate font-semibold text-gray-900">
                                                        {ticket.reference || ticket.id}
                                                    </h3>
                                                </div>
                                                <p className="mt-1 break-words text-sm text-gray-600">
                                                    {ticket.expand?.CompanyID?.CompanyName || "-"}
                                                </p>
                                            </div>
                                            <div>{renderStars(ticket.priority)}</div>
                                        </div>
                                        <div className="mt-4 space-y-2 text-sm text-gray-600">
                                            <p><span className="font-medium text-gray-900">Initiator:</span> {ticket.Initiator || "-"}</p>
                                            <p><span className="font-medium text-gray-900">Support:</span> {ticket.expand?.SupportMember ? `${ticket.expand.SupportMember.Name} ${ticket.expand.SupportMember.Surname}` : "-"}</p>
                                            <p><span className="font-medium text-gray-900">Created:</span> {new Date(ticket.created).toLocaleString()}</p>
                                        </div>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                                                !ticket.TicketClose ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                            }`}>
                                                {!ticket.TicketClose ? 'Open' : 'Closed'}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })
                        ) : (
                            <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-gray-500">
                                <div className="text-lg font-medium mb-2">No tickets found</div>
                                <div className="text-sm">Try changing the filter status</div>
                            </div>
                        )}
                    </div>

                    {/* Table Container */}
                    <div className="hidden flex-1 overflow-auto md:block">
                        <table className="min-w-[900px] w-full">
                            <thead className="sticky top-0 bg-gray-50 z-10">
                                <tr>
                                    <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Reference</th>
                                    <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Priority</th>
                                    <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Company</th>
                                    <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Initiator</th>
                                    <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Support Member</th>
                                    <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Created</th>
                                    <th className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    Array.from({ length: 8 }).map((_, index) => (
                                        <SkeletonRow key={index} />
                                    ))
                                ) : paginatedTickets.length > 0 ? (
                                    paginatedTickets.map((ticket) => {
                                        const needsReply = ticket.is_support_reply !== true;
                                        return (
                                            <tr 
                                                key={ticket.id} 
                                                className={`border-b transition-colors duration-200 cursor-pointer hover:bg-gray-50 ${
                                                    needsReply ? 'bg-green-50/40' : ''
                                                }`}
                                                onClick={() => navigate(`/tickets/${ticket.id}`)}
                                            >
                                                <td className="py-4 px-6">
                                                    <div className="flex items-center gap-2">
                                                        {needsReply && (
                                                            <FaCircle 
                                                                className="text-[9px] text-green-500 flex-shrink-0"
                                                                title="Needs Reply"
                                                            />
                                                        )}
                                                        <span className="text-gray-900 font-medium">
                                                            {ticket.reference || ticket.id}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-6">
                                                    {renderStars(ticket.priority)}
                                                </td>
                                                <td className="py-4 px-6">
                                                    <div className="text-gray-700 font-medium">
                                                        {ticket.expand?.CompanyID?.CompanyName || "-"}
                                                    </div>
                                                </td>
                                                <td className="py-4 px-6">
                                                    <div className="text-gray-700">
                                                        {ticket.Initiator || "-"}
                                                    </div>
                                                </td>
                                                <td className="py-4 px-6">
                                                    <div className="text-gray-700">
                                                        {ticket.expand?.SupportMember 
                                                            ? `${ticket.expand.SupportMember.Name} ${ticket.expand.SupportMember.Surname}` 
                                                            : "-"}
                                                    </div>
                                                </td>
                                                <td className="py-4 px-6">
                                                    <div className="text-gray-700">
                                                        {new Date(ticket.created).toLocaleString()}
                                                    </div>
                                                </td>
                                                <td className="py-4 px-6">
                                                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                                                        !ticket.TicketClose 
                                                            ? 'bg-green-100 text-green-700' 
                                                            : 'bg-gray-100 text-gray-700'
                                                    }`}>
                                                        {!ticket.TicketClose ? 'Open' : 'Closed'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="py-12 px-6 text-center">
                                            <div className="text-gray-500">
                                                <div className="text-lg font-medium mb-2">No tickets found</div>
                                                <div className="text-sm">Try changing the filter status</div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {!isLoading && tickets.length > PAGE_SIZE && (
                        <div className="px-4 sm:px-6 py-4 border-t border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-sm text-gray-500">
                                Page <span className="font-semibold text-gray-700">{currentPage}</span> of{' '}
                                <span className="font-semibold text-gray-700">{totalPages}</span>
                            </p>

                            <div className="flex flex-wrap items-center gap-1">
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FaChevronLeft className="text-xs" />
                                </button>

                                {getPageNumbers()[0] > 1 && (
                                    <>
                                        <button
                                            onClick={() => goToPage(1)}
                                            className="w-9 h-9 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                                        >
                                            1
                                        </button>
                                        {getPageNumbers()[0] > 2 && (
                                            <span className="px-1 text-gray-400 text-sm">…</span>
                                        )}
                                    </>
                                )}

                                {getPageNumbers().map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => goToPage(page)}
                                        className={`w-9 h-9 rounded-lg border text-sm font-medium transition-colors ${
                                            page === currentPage
                                                ? 'bg-[#90c740] border-[#90c740] text-white'
                                                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
                                    <>
                                        {getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && (
                                            <span className="px-1 text-gray-400 text-sm">…</span>
                                        )}
                                        <button
                                            onClick={() => goToPage(totalPages)}
                                            className="w-9 h-9 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                                        >
                                            {totalPages}
                                        </button>
                                    </>
                                )}

                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FaChevronRight className="text-xs" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default CompanyTicketsView;
